import React from 'react';

type SaleMarket = {
  average?: number | null;
  median?: number | null;
  min?: number | null;
  max?: number | null;
  averagePerSqFt?: number | null;
  medianPerSqFt?: number | null;
  averageSquareFootage?: number | null;
  medianSquareFootage?: number | null;
  averageDaysOnMarket?: number | null;
  medianDaysOnMarket?: number | null;
  minDaysOnMarket?: number | null;
  maxDaysOnMarket?: number | null;
};

const money = (value: number | null | undefined, decimals = 0) =>
  value != null && Number.isFinite(Number(value))
    ? `$${Number(value).toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`
    : '—';

const num = (value: number | null | undefined, decimals = 0) =>
  value != null && Number.isFinite(Number(value))
    ? Number(value).toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
    : '—';

const RangeRow: React.FC<{
  number: number;
  title: string;
  icon: string;
  min?: number | null;
  max?: number | null;
  median?: number | null;
  average?: number | null;
  format: (value: number | null | undefined) => string;
}> = ({ number, title, icon, min, max, median, average, format }) => {
  const low = Number(min) || 0;
  const high = Number(max) || 0;
  const span = Math.max(high - low, 1);
  const position = (value: number | null | undefined) =>
    `${Math.max(0, Math.min(100, ((Number(value) - low) / span) * 100))}%`;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '78px 1fr', gap: 14, border: '1px solid #e7edf2', borderRadius: 12, padding: 16 }}>
      <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#e8f7f7', color: '#168f9a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28 }}>
        {icon}
      </div>
      <div>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#17233d', marginBottom: 10 }}>{number}. {title}</div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, minmax(100px, 1fr))',
            gap: 10,
            marginBottom: 10,
          }}
        >
          <div style={{ fontSize: 10, color: '#64748b' }}>Min<div style={{ fontSize: 13, fontWeight: 800, color: '#17233d' }}>{format(min)}</div></div>
          <div style={{ fontSize: 10, color: '#157a6e', textAlign: 'center' }}>Median<div style={{ fontSize: 15, fontWeight: 800 }}>{format(median)}</div></div>
          <div style={{ fontSize: 10, color: '#176fc1', textAlign: 'center' }}>Average<div style={{ fontSize: 15, fontWeight: 800 }}>{format(average)}</div></div>
          <div style={{ fontSize: 10, color: '#64748b', textAlign: 'right' }}>Max<div style={{ fontSize: 13, fontWeight: 800, color: '#17233d' }}>{format(max)}</div></div>
        </div>
        <div style={{ position: 'relative', height: 24, margin: '0 8px' }}>
          <div style={{ position: 'absolute', left: 0, right: 0, top: 10, height: 3, background: '#cbd5e1' }} />
          <div style={{ position: 'absolute', left: 0, top: 5, width: 11, height: 11, borderRadius: '50%', border: '2px solid #b8c3d2', background: '#fff' }} />
          <div style={{ position: 'absolute', right: 0, top: 5, width: 11, height: 11, borderRadius: '50%', border: '2px solid #b8c3d2', background: '#fff' }} />
          <div style={{ position: 'absolute', left: position(median), top: 4, transform: 'translateX(-50%)' }}>
            <div style={{ width: 13, height: 13, borderRadius: '50%', background: '#1098a0', boxShadow: '0 0 0 3px rgba(16,152,160,.12)' }} />
          </div>
          <div style={{ position: 'absolute', left: position(average), top: 4, transform: 'translateX(-50%)' }}>
            <div style={{ width: 13, height: 13, borderRadius: '50%', background: '#2778dd', boxShadow: '0 0 0 3px rgba(39,120,221,.12)' }} />
          </div>
        </div>
      </div>
    </div>
  );
};

const BenchmarkRow: React.FC<{
  number: number;
  title: string;
  icon: string;
  median: string;
  average: string;
}> = ({ number, title, icon, median, average }) => (
  <div style={{ display: 'grid', gridTemplateColumns: '78px 1fr', gap: 14, border: '1px solid #e7edf2', borderRadius: 12, padding: 16 }}>
    <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#e8f7f7', color: '#168f9a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28 }}>{icon}</div>
    <div>
      <div style={{ fontSize: 15, fontWeight: 700, color: '#17233d', marginBottom: 12 }}>{number}. {title}</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        <div style={{ textAlign: 'center', borderRight: '1px solid #e5e7eb' }}>
          <div style={{ color: '#157a6e', fontSize: 10 }}>Median</div>
          <div style={{ color: '#157a6e', fontWeight: 800, fontSize: 20 }}>{median}</div>
          <div style={{ height: 2, background: '#1098a0', margin: '8px auto 0', width: 138 }}><span style={{ display: 'block', width: 12, height: 12, borderRadius: '50%', background: '#1098a0', margin: '-5px auto' }} /></div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ color: '#176fc1', fontSize: 10 }}>Average</div>
          <div style={{ color: '#176fc1', fontWeight: 800, fontSize: 20 }}>{average}</div>
          <div style={{ height: 2, background: '#2778dd', margin: '8px auto 0', width: 138 }}><span style={{ display: 'block', width: 12, height: 12, borderRadius: '50%', background: '#2778dd', margin: '-5px auto' }} /></div>
        </div>
      </div>
    </div>
  </div>
);

const MarketBenchmarksCard: React.FC<{ sale?: SaleMarket | null }> = ({ sale }) => {
  if (!sale) return null;
  return (
    <div style={{ border: '1px solid #e8eef3', borderRadius: 14, background: '#fff', boxShadow: '0 2px 10px rgba(15,23,42,.06)', padding: 16, marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#12858d', fontSize: 18, fontWeight: 800, marginBottom: 14 }}>
        <span style={{ width: 28, height: 28, borderRadius: '50%', background: '#dff4f5', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>↗</span>
        8. Market Benchmarks &amp; Range Bars
      </div>
      <div style={{ display: 'grid', gap: 10 }}>
        <RangeRow number={1} title="Sale Price Range" icon="◇" min={sale.min} max={sale.max} median={sale.median} average={sale.average} format={(value) => money(value)} />
        <RangeRow number={2} title="Days on Market Range" icon="▣" min={sale.minDaysOnMarket} max={sale.maxDaysOnMarket} median={sale.medianDaysOnMarket} average={sale.averageDaysOnMarket} format={(value) => num(value, 2)} />
        <BenchmarkRow number={3} title="$/Sq Ft Benchmark" icon="◇" median={money(sale.medianPerSqFt, 2)} average={money(sale.averagePerSqFt, 2)} />
        <BenchmarkRow number={4} title="Sq Footage Benchmark" icon="▦" median={`${num(sale.medianSquareFootage)} sq ft`} average={`${num(sale.averageSquareFootage)} sq ft`} />
      </div>
    </div>
  );
};

export default MarketBenchmarksCard;
